// One RF sensor, in the room, as a tile.
//
// It reads like the other sensor tiles on purpose — a customer should not have
// to know that a safe-locker sensor has no unique number and lives in a
// different table. What it shows is the last event HEARD, with its age.
//
// ⚠️ NEVER A BARE STATE. Nothing acknowledges an RF frame, so a lost "Close"
// burst leaves "Open" standing for ever. The age is part of the answer, and
// after RF_STALE_MS the tile goes grey and says how long it has been: the app
// may say what it last heard, never what the door is doing now.

import React from 'react';
import {
  Box, Card, CardActionArea, Typography, useTheme,
} from '@mui/material';
import { rfType, isCloseEvent } from '../../core/devices/rfTypes';
import { rfStateIsStale } from '../../core/devices/rfCodes';
import { parseServerTime } from '../../core/devices/firmwareInfo';

const NORMAL = '#2E7D32';
const ALERT = '#C62828';
const NEUTRAL = '#9E9E9E';

export const rfAgo = (raw, now = Date.now()) => {
  const t = parseServerTime(raw);
  if (t == null) return 'never';
  const m = Math.floor((now - t) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  return h < 48 ? `${h} h ago` : `${Math.floor(h / 24)} d ago`;
};

/**
 * The colour of a state.
 *
 * ⚠️ OPEN IS THE ALERT, CLOSE IS THE REST — for a door, a safe or a shutter.
 * A single-event sensor (fire, motion, panic) has nothing to be "at rest" in:
 * hearing from it at all is the alert, until it goes stale. An OPENS-ONLY
 * sensor (window) is the same: it never reports closing, so its event reads
 * "Opened" — a thing that happened, with its age — never "Open", a state.
 */
export function rfTileState(device, now = Date.now()) {
  const event = String(device?.LastEvent || '').trim();
  const at = parseServerTime(device?.LastAt);
  if (at == null) return { colour: NEUTRAL, label: 'No report yet', stale: false };
  const label = rfType(device?.RfType).openOnly && /^open$/i.test(event) ? 'Opened' : event;
  const stale = rfStateIsStale(at, now);
  if (stale) return { colour: NEUTRAL, label: label || 'heard', stale: true };
  if (!event) return { colour: NEUTRAL, label: 'heard', stale: false };
  return { colour: isCloseEvent(event) ? NORMAL : ALERT, label, stale: false };
}

export default function RfSensorTile({
  device, onOpen, onMenu, compact = false, dense = false, pill = false,
}) {
  const theme = useTheme();
  const c = theme.sli || {};
  const t = rfType(device.RfType);
  const { colour, label, stale } = rfTileState(device);
  const name = String(device.Name || '').trim() || t.label;
  // Right-click = every setting of the sensor (ui/shared/RfSensorMenu), the
  // desktop gesture the device cards' buttons already use. Without a host
  // handler the browser's own menu is left alone.
  const menu = onMenu
    ? (e) => { e.preventDefault(); onMenu(device, e.clientX + 2, e.clientY - 6); }
    : undefined;

  // PILL (the desktop room header, 2026-10-13 "don't waste space"): the same
  // three facts — state, name, age — on ONE line, so the sensors sit in the
  // room's header row beside the lock and the scenes instead of a row of cards.
  if (pill) {
    return (
      <Box
        role="button"
        tabIndex={0}
        onClick={() => onOpen && onOpen(device)}
        onKeyDown={(e) => { if (e.key === 'Enter' && onOpen) onOpen(device); }}
        onContextMenu={menu}
        title={`${name} — ${label} ${rfAgo(device.LastAt)}`}
        sx={{
          display: 'inline-flex', alignItems: 'center', gap: 0.6, maxWidth: 260,
          bgcolor: c.button || 'primary.main', borderRadius: 5, pl: 0.4, pr: 1, py: 0.3,
          cursor: 'pointer', userSelect: 'none', boxShadow: 2,
          '&:hover': { filter: 'brightness(1.08)' },
        }}
      >
        <Box sx={{
          width: 20, height: 20, flexShrink: 0, borderRadius: '50%', bgcolor: colour,
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11,
        }}
        >
          {t.icon}
        </Box>
        <Typography noWrap sx={{ color: c.text || 'text.primary', fontSize: 11.5, fontWeight: 600, minWidth: 0 }}>
          {name}
        </Typography>
        <Typography noWrap sx={{ color: colour, fontWeight: 800, fontSize: 10.5, textTransform: 'uppercase', flexShrink: 0 }}>
          {label}
        </Typography>
        {/* The age is not decoration — see the note at the top. */}
        <Typography noWrap sx={{ color: c.text || 'text.secondary', opacity: stale ? 0.9 : 0.6, fontSize: 10, flexShrink: 0 }}>
          {rfAgo(device.LastAt).replace(' ago', '')}
        </Typography>
      </Box>
    );
  }

  // DENSE (the desktop, 2026-10-08): icon beside the text — the same three
  // facts (state, name, age) in a card about half as tall.
  if (dense) {
    return (
      <Card elevation={3} sx={{ bgcolor: c.button || 'primary.main', borderRadius: '12px', height: '100%' }}>
        <CardActionArea
          onClick={() => onOpen && onOpen(device)}
          onContextMenu={menu}
          sx={{
            height: '100%', borderRadius: '12px', display: 'flex', alignItems: 'center',
            justifyContent: 'flex-start', gap: 1, px: 1, py: 0.75,
          }}
        >
          <Box
            sx={{
              width: 30, height: 30, flexShrink: 0, borderRadius: '50%', bgcolor: colour,
              boxShadow: `0 0 0 3px ${colour}33`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15,
            }}
          >
            {t.icon}
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ color: colour, fontWeight: 800, fontSize: 11, textTransform: 'uppercase', lineHeight: 1.15 }} noWrap>
              {label}
            </Typography>
            <Typography sx={{ color: c.text || 'text.primary', fontSize: 11.5, fontWeight: 600, lineHeight: 1.2 }} noWrap>
              {name}
            </Typography>
            {/* The age is not decoration — see the note at the top. */}
            <Typography sx={{ color: c.text || 'text.secondary', opacity: stale ? 0.9 : 0.6, fontSize: 10, lineHeight: 1.2 }} noWrap>
              {rfAgo(device.LastAt)}
            </Typography>
          </Box>
        </CardActionArea>
      </Card>
    );
  }

  return (
    <Card
      elevation={6}
      sx={{ bgcolor: c.button || 'primary.main', borderRadius: '20px', height: '100%' }}
    >
      <CardActionArea
        onClick={() => onOpen && onOpen(device)}
        onContextMenu={menu}
        sx={{
          height: '100%', borderRadius: '20px', display: 'flex',
          alignItems: 'center', justifyContent: 'center', p: 1.5,
        }}
      >
        <Box sx={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%',
        }}
        >
          <Box
            sx={{
              width: compact ? 36 : 44,
              height: compact ? 36 : 44,
              borderRadius: '50%',
              bgcolor: colour,
              boxShadow: `0 0 0 6px ${colour}33`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: compact ? 18 : 22,
              mb: 0.75,
            }}
          >
            {t.icon}
          </Box>
          <Typography
            sx={{
              color: colour, fontWeight: 800, fontSize: compact ? 12 : 14,
              textTransform: 'uppercase', lineHeight: 1.1, textAlign: 'center',
            }}
          >
            {label}
          </Typography>
          <Typography
            sx={{
              color: c.text || 'text.primary', fontSize: compact ? 11 : 12,
              textAlign: 'center', mt: 0.25,
            }}
            noWrap
          >
            {name}
          </Typography>
          {/* The age is not decoration — see the note at the top. */}
          <Typography
            sx={{
              color: c.text || 'text.secondary',
              opacity: stale ? 0.9 : 0.6,
              fontSize: 10.5,
              textAlign: 'center',
            }}
          >
            {rfAgo(device.LastAt)}
          </Typography>
        </Box>
      </CardActionArea>
    </Card>
  );
}
